<template>
  <div class="row g-2 justify-content-center my-5">
    <div class="col-xxl-6 col-xl-7 col-lg-8 col-md-11">
      <p>21. Juli 2025 von Theo Döllmann</p>
      <h1>Nach der Generalsanierung der Riedbahn ist eine kleine Verbesserung erkennbar</h1>
      <p>
        Im Rahmen einer wissenschaftlichen Arbeit an der Uni untersuche ich derzeit räumliche
        Verspätungsmuster. Das habe ich als Anlass genommen, um eine Untersuchung zur Auswirkung der
        Generalsanierung auf der Riedbahn durchzuführen. In diesem Blogbeitrag versuche ich genau so
        viel Kontext zu Verspätungen und der Generalsanierung zu geben, dass die Ergebnisse
        verständlich sind, ohne aber jedes letzte Detail genau auszuschmücken.
      </p>
      <p>
        <b>TLDR:</b> Ein halbes Jahr nach der Generalsanierung ist die Laufzeitverspätung auf der
        Riedbahn zurückgegangen. Ob die Generalsanierung für die Verbesserung verantwortlich ist,
        lässt sich aus den Daten allerdings nicht ablesen.
      </p>

      <h2>Laufzeit- vs. Haltezeitverspätung</h2>
      <p>
        Züge können auf zwei Arten Verspätung sammeln: entweder beim Halt an einem Bahnhof oder auf
        der Strecke zwischen den Halten. Sehr vereinfacht betrachtet sind Halteverspätungen eher
        Ereignissen am Halt geschuldet, während Laufzeitverspätungen meist auf Störungen auf der
        Strecke beruhen. Natürlich gibt es viele und häufige Ausnahmen, und die beiden
        Verspätungsarten bedingen sich auch gegenseitig.
      </p>
      <p>
        Durch die Generalsanierung der Riedbahn wurde die Strecke zwischen Mannheim und Frankfurt
        erneuert. Mit der vereinfachten Verspätungsvorstellung von Haltezeit- und
        Laufzeitverspätungen gehe ich davon aus, dass sich durch die Generalsanierung vor allem die
        Laufzeitverspätung verbessert hat. Zwar wurden auch einige Bahnhöfe modernisiert, allerdings
        halte ich es für unwahrscheinlich, dass dadurch zum Beispiel das Ein- und Aussteigen
        merklich schneller wird.
      </p>
      <p>Für die folgende Analyse betrachten wir aus diesen Gründen nur die Laufzeitverspätung.</p>

      <h2>Analysezeitraum</h2>
      <p>
        Die Generalsanierung der Riedbahn wurde vom 15. Juli bis zum 15. Dezember 2024 durchgeführt.
        Zwischen dem 1. und 22. Januar 2024 war die Strecke bereits für Vorbereitungsarbeiten
        gesperrt. Um den erhofften Verbesserungseffekt der Sanierungsarbeiten zu prüfen, habe ich
        jeweils einen Testzeitraum vor und nach den Arbeiten definiert. Damit potenzielle saisonale
        Muster möglichst keine Rolle spielen, verwende ich jeweils den gleichen Zeitraum.
      </p>
      <p><b>Vor der Generalsanierung:</b> 23. Jan. – 14. Juli 2024</p>
      <p><b>Nach der Generalsanierung:</b> 23. Jan. – 14. Juli 2025</p>

      <h2>Wo fährt der Zug?</h2>
      <p>
        Ich möchte die Verspätungen gerne auf einer Karte anzeigen. Die eigentlich triviale Frage,
        auf welcher Strecke ein bestimmter Zug genau fährt, ist aus den Daten nicht eindeutig
        ableitbar. In den Daten sind nur die Halte der Züge enthalten. Anhand der Zugkategorie und
        des Schienennetzes von OpenStreetMap leite ich die vermutlich gefahrene Strecke ab. Die
        Verspätung des Zuges auf dieser Strecke wird dann gleichmäßig auf die einzelnen
        Streckensegmente verteilt. Das funktioniert im Allgemeinen gut. Wird aber beispielsweise ein
        Zug umgeleitet, fließt er fehlerhaft in die Statistik ein – ein Problem, das sich mit den
        vorliegenden Daten nicht sauber lösen lässt.
      </p>

      <h2>Ergebnisse</h2>
      <p>
        In <a href="#figure_1" class="link-light fst-italic">Abbildung 1</a> ist die
        durchschnittliche Verspätung auf der Riedbahn vor und nach der Generalsanierung abgebildet.
        Die Riedbahn ist die mittlere der in der Abbildung gezeigten Zugstrecken, die Mannheim
        direkt mit Frankfurt verbindet. Je deutlicher die Umrandung der Strecke, desto mehr Verkehr
        weist sie auf.
      </p>
      <figure id="figure_1" class="figure">
        <img
          :src="resultAllImg"
          alt="Eine Karte zeigt die Laufzeitverspätung auf der Riedbahn vor und nach der Generalsanierung. Nach der Sanierung ist die Verspätung gesunken, allerdings nicht stark."
          class="figure-img img-fluid rounded"
        />
        <figcaption class="figure-caption">
          Abbildung 1: Laufzeitverspätung auf der Riedbahn für den gesamten Zugverkehr. Daten
          gesammelt von der Deutschen Bahn. Streckennetz von OpenStreetMap. Da es sich um eine
          Auswertung Dritter handelt, sind die Ergebnisse mit Vorsicht zu genießen.
        </figcaption>
      </figure>
      <p>
        Insgesamt ergibt sich in
        <a href="#figure_1" class="link-light fst-italic">Abbildung 1</a> ein gemischtes Bild. Auf
        vielen Strecken wird eher Verspätung gesammelt (Rottöne), auf anderen eher Verspätung
        abgebaut (Blautöne), und bei wieder anderen bleibt die Verspätung im Schnitt konstant
        (Schwarz).
      </p>
      <p>
        Vergleicht man die Farbtöne auf der Riedbahn vor und nach der Generalsanierung, zeigt sich
        eine leichte Verbesserung: Die Rottöne sind weniger intensiv. Nach meiner Einschätzung kommt
        nach der Sanierung etwa eine Sekunde weniger Verspätung pro Fahrtkilometer hinzu. Dennoch
        sorgt die Riedbahn nach wie vor tendenziell für Verspätungen.
      </p>

      <h3>Fernverkehr</h3>
      <p>
        Glaubt man der Deutschen Bahn, die die Riedbahn als „Aorta“ des deutschen Schienennetzes
        bezeichnet, spielt sie im Fernverkehr eine besondere Rolle. Daher habe ich neben dem
        Gesamtverkehr auch den Fernverkehr separat betrachtet. Er ist in
        <a href="#figure_2" class="link-light fst-italic">Abbildung 2</a>
        dargestellt. Dabei fällt sofort auf, dass der Fernverkehr auf weniger Strecken verkehrt und
        die Farben aufgrund der längeren Streckenabschnitte weniger stark variieren.
      </p>
      <figure id="figure_2" class="figure">
        <img
          :src="resultLongDistanceImg"
          alt="Eine Karte zeigt die Laufzeitverspätung auf der Riedbahn für den Fernverkehr vor und nach der Generalsanierung. Nach der Sanierung ist die Verspätung gesunken, allerdings nicht stark."
          class="figure-img img-fluid rounded"
        />
        <figcaption class="figure-caption">
          Abbildung 2: Laufzeitverspätung auf der Riedbahn für den Fernverkehr. Daten gesammelt von
          der Deutschen Bahn. Streckennetz von OpenStreetMap. Da es sich um eine Auswertung Dritter
          handelt, sind die Ergebnisse mit Vorsicht zu genießen.
        </figcaption>
      </figure>
      <p>
        In
        <a href="#figure_2" class="link-light fst-italic">Abbildung 2</a> ist, wie auch im
        Gesamtverkehr, ein leichter Rückgang der Verspätung zu erkennen. Meiner Einschätzung nach
        sinkt sie hier von etwa 4 Sekunden auf etwa 2 Sekunden pro Kilometer. Genaue Zahlen liefere
        ich absichtlich nicht, da exakte Daten in diesem Fall vermutlich nur von der Bahn selbst
        stammen können. Die Reduktion wirkt jedoch deutlich. Erkennbar ist auf dieser Karte
        allerdings nicht, dass die Riedbahn damit weiterhin zu den verspätungsanfälligsten
        Fernverkehrsstrecken zählt.
      </p>

      <h2>Fazit</h2>
      <p>
        Nach der Generalsanierung hat sich die Zuverlässigkeit der Riedbahn verbessert. Ob dies
        allerdings auf die Sanierung selbst zurückzuführen ist, lässt sich aus den Daten nicht
        ablesen. Die Verbesserung könnte Zufall sein oder zum Beispiel auf Fahrplanänderungen wie
        großzügigere Puffer zurückgehen. Ich persönlich halte es für wahrscheinlich, dass die
        Generalsanierung der Riedbahn tatsächlich einen positiven Effekt hatte.
      </p>
      <p>
        Langfristig werden Generalsanierungen allein für eine klimagerechte Verkehrswende nicht
        ausreichen. Um Menschen vom Auto auf die Bahn zu bringen, reicht eine reine
        Angebotsverbesserung nicht aus. Aktuell können wir den Sanierungsstau der Autobahnen dazu
        nutzen, um zu überlegen, was wir wo sanieren, welche Brücken wir wieder aufbauen, wo sich
        eine Autobahn in eine Bundesstraße mit weniger Spuren umwandeln lässt und wie wir mit dem
        gewonnenen Geld und dem neu gewonnenen Platz die Bahn weiter ausbauen können.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'default' })
import resultAllImg from '~/assets/img/blog/Riedbahn_Generalsanierung.avif'
import resultLongDistanceImg from '~/assets/img/blog/Riedbahn_Generalsanierung_Fernverkehr.avif'

useHead({
  title: 'Blog: Riedbahn Generalsanierung',
  meta: [
    {
      name: 'description',
      content:
        'Blog: Wie sich die Generalsanierung der Riedbahn auf die Verspätungen auf der Strecke ausgewirkt hat.'
    }
  ]
})
</script>
